import type { FormEvent } from 'react';
import {
  type OverlayTriggerState,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  PrimaryField,
  selectors,
  Small,
  styled,
  Text,
  TextInput,
  useDialogState,
} from '@meterup/atto';
import { useCallback, useEffect, useState } from 'react';

import type { TablePresetsState } from '../utils';
import { ConfirmDialog } from '../../Dialogs/ConfirmDialog';
import { DestructiveDialog } from '../../Dialogs/DestructiveDialog';
import { notify } from '../../Notifications';

interface TablePresetsNameDialogProps {
  onSave: (label: string) => void;
  state: OverlayTriggerState;
  initialLabel?: string;
  heading?: string;
  submitLabel?: string;
}

function TablePresetsNameDialog({
  onSave,
  state,
  initialLabel = '',
  heading = 'Save preset',
  submitLabel = 'Save',
}: TablePresetsNameDialogProps) {
  const [label, setLabel] = useState(initialLabel);

  useEffect(() => {
    setLabel(initialLabel);
  }, [initialLabel]);

  const handleClose = useCallback(() => {
    state.close();
    setLabel('');
  }, [state]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (label.trim()) {
        onSave(label.trim());
        setLabel('');
      }
    },
    [label, onSave],
  );

  return (
    <Dialog preset="narrow" state={state}>
      <form onSubmit={handleSubmit}>
        <DialogHeader icon="save" heading={heading} />
        <DialogContent>
          <PrimaryField
            label="Label"
            element={<TextInput value={label} onChange={(v) => setLabel(v)} autoFocus />}
          />
        </DialogContent>
        <DialogFooter
          end={
            <>
              <Button type="button" variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={!label.trim()}>
                {submitLabel}
              </Button>
            </>
          }
        />
      </form>
    </Dialog>
  );
}

interface TablePresetsDefaultDialogProps {
  label: string;
  onSetDefault: () => void;
  state: OverlayTriggerState;
}

function TablePresetsDefaultDialog({ onSetDefault, state, label }: TablePresetsDefaultDialogProps) {
  return (
    <ConfirmDialog
      state={state}
      icon="reserve"
      heading="Set as default"
      alert={{
        variant: 'neutral',
        icon: 'question',
        heading: <Text>Defaults override filters and columns</Text>,
        copy: (
          <Text>
            <Text weight="bold">{label}</Text> will be applied as the default preset for this page
            meaning that any filters and columns you have set will be overridden whenever you return
            to this page.
          </Text>
        ),
      }}
      handleConfirm={onSetDefault}
      confirmLabel="Set as default"
    />
  );
}

interface TablePresetsDeleteDialogProps {
  label: string;
  onDelete: () => void;
  state: OverlayTriggerState;
}

function TablePresetsDeleteDialog({ onDelete, state, label }: TablePresetsDeleteDialogProps) {
  return (
    <DestructiveDialog
      state={state}
      handleDelete={onDelete}
      label={label}
      alert={{
        copy: (
          <Text>
            Are you sure you want to delete <Text weight="bold">{label}</Text>?
          </Text>
        ),
      }}
    />
  );
}

const TablePresetsItemActions = styled(DropdownMenuButton, {
  margin: '$2 0',
  opacity: '$$tablePresetsItemActionsOpacity',

  '&[data-state="open"]': {
    opacity: 1,
  },
});

const TablePresetsItemContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
  alignItems: 'flex-start',
  width: '100%',
  userSelect: 'none',
  $$tablePresetsItemActionsOpacity: 0,

  [selectors.hover]: {
    $$tablePresetsItemActionsOpacity: 1,
  },
});

interface TablePresetsItemProps {
  preset: { id: string; name: string };
  isDefault: boolean;
  onSetDefault: () => void;
  onClearDefault: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function TablePresetsItem({
  preset,
  isDefault,
  onSetDefault,
  onClearDefault,
  onRename,
  onDuplicate,
  onDelete,
}: TablePresetsItemProps) {
  return (
    <TablePresetsItemContainer>
      <DropdownMenuRadioItem
        value={preset.id}
        badge={isDefault ? { label: 'Default' } : undefined}
        style={{ width: '100%' }}
      >
        {preset.name}
      </DropdownMenuRadioItem>
      <DropdownMenu>
        <TablePresetsItemActions
          onClick={(e) => e.stopPropagation()}
          arrangement="hidden-label"
          icon="overflow-horizontal"
          ghost
          size="small"
          variant="secondary"
        >
          Actions
        </TablePresetsItemActions>
        <DropdownMenuPopover align="start" side="right">
          <DropdownMenuGroup>
            {!isDefault && (
              <DropdownMenuItem onClick={onSetDefault} icon="reserve">
                Set as default
              </DropdownMenuItem>
            )}
            {isDefault && (
              <DropdownMenuItem onClick={onClearDefault} icon="cross-circle" variant="negative">
                Remove as default
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={onRename} icon="pencil">
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate} icon="copy">
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} icon="trash-can" variant="negative">
              Delete
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuPopover>
      </DropdownMenu>
    </TablePresetsItemContainer>
  );
}

export interface TablePresetsProps {
  presets: TablePresetsState;
  disabled?: boolean;
}

export function TablePresets({ presets: presetsState, disabled = false }: TablePresetsProps) {
  const {
    presets,
    defaultPresetId,
    selectedPresetId,
    isPresetDirty,
    renamePreset,
    deletePreset,
    duplicatePreset,
    setDefaultPreset,
    clearDefaultPreset,
    applyPreset: onPresetApply,
    saveCurrentAsPreset: onSaveCurrentAsPreset,
    updateSelectedPreset: onUpdatePreset,
    clearPreset: onPresetClear,
  } = presetsState;
  const [presetToRename, setPresetToRename] = useState<{ id: string; name: string } | null>(null);
  const [presetToDuplicate, setPresetToDuplicate] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [presetToDelete, setPresetToDelete] = useState<{ id: string; name: string } | null>(null);
  const [presetToSetDefault, setPresetToSetDefault] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const mainDropdownState = useDialogState();
  const saveDialogState = useDialogState();
  const renameDialogState = useDialogState();
  const duplicateDialogState = useDialogState();
  const deleteDialogState = useDialogState();
  const defaultDialogState = useDialogState();

  const handleSavePreset = useCallback(
    (label: string) => {
      onSaveCurrentAsPreset(label);
      notify(`Preset ${label} has been saved.`, {
        variant: 'positive',
      });
      saveDialogState.state.close();
    },
    [onSaveCurrentAsPreset, saveDialogState],
  );

  const handleRenamePreset = useCallback(
    (preset: { id: string; name: string }) => {
      setPresetToRename(preset);
      mainDropdownState.state.close();
      renameDialogState.openFromMenu();
    },
    [mainDropdownState, renameDialogState],
  );

  const handleConfirmRename = useCallback(
    (name: string) => {
      if (presetToRename) {
        renamePreset(presetToRename.id, name);
        notify(`Preset renamed to ${name}.`, { variant: 'positive' });
        renameDialogState.state.close();
        setPresetToRename(null);
      }
    },
    [presetToRename, renamePreset, renameDialogState],
  );

  const handleDuplicatePreset = useCallback(
    (preset: { id: string; name: string }) => {
      setPresetToDuplicate(preset);
      mainDropdownState.state.close();
      duplicateDialogState.openFromMenu();
    },
    [mainDropdownState, duplicateDialogState],
  );

  const handleConfirmDuplicate = useCallback(
    (name: string) => {
      if (presetToDuplicate) {
        duplicatePreset(presetToDuplicate.id, name);
        notify(`Preset ${name} has been created.`, {
          variant: 'positive',
        });
        duplicateDialogState.state.close();
        setPresetToDuplicate(null);
      }
    },
    [presetToDuplicate, duplicatePreset, duplicateDialogState],
  );

  const handleDeletePreset = useCallback(
    (presetId: string) => {
      const preset = presets.find((p) => p.id === presetId);
      if (preset) {
        setPresetToDelete(preset);
        mainDropdownState.state.close();
        deleteDialogState.openFromMenu();
      }
    },
    [presets, mainDropdownState, deleteDialogState],
  );

  const handleConfirmDelete = useCallback(() => {
    if (presetToDelete) {
      deletePreset(presetToDelete.id);
      notify(`Successfully deleted preset ${presetToDelete.name}.`, {
        variant: 'positive',
      });
      deleteDialogState.state.close();
      setPresetToDelete(null);
    }
  }, [presetToDelete, deletePreset, deleteDialogState]);

  const handleSetDefault = useCallback(
    (presetId: string) => {
      const preset = presets.find((p) => p.id === presetId);
      if (preset) {
        setPresetToSetDefault(preset);
        mainDropdownState.state.close();
        defaultDialogState.openFromMenu();
      }
    },
    [presets, mainDropdownState, defaultDialogState],
  );

  const handleConfirmSetDefault = useCallback(() => {
    if (presetToSetDefault) {
      setDefaultPreset(presetToSetDefault.id);
      notify(`${presetToSetDefault.name} is now the default preset.`, {
        variant: 'positive',
      });
      defaultDialogState.state.close();
      setPresetToSetDefault(null);
    }
  }, [presetToSetDefault, setDefaultPreset, defaultDialogState]);

  const handleClearDefault = useCallback(() => {
    clearDefaultPreset();
    notify('Default preset has been cleared.', {
      variant: 'positive',
    });
  }, [clearDefaultPreset]);

  return (
    <>
      <DropdownMenu
        open={mainDropdownState.state.isOpen}
        onOpenChange={mainDropdownState.state.setOpen}
      >
        <DropdownMenuButton
          disabled={disabled}
          arrangement="hidden-label"
          appearance="muted"
          callout={isPresetDirty ? { variant: 'brand', label: 'Unsaved changes' } : undefined}
          icon={selectedPresetId ? 'bookmark-filled' : 'bookmark'}
          size="small"
          variant={selectedPresetId ? 'primary' : 'secondary'}
        >
          Presets
        </DropdownMenuButton>
        <DropdownMenuPopover align="start" maxWidth={280}>
          {presets.length === 0 ? (
            <DropdownMenuGroup label="Presets">
              <DropdownMenuItem disabled>
                <Small>No saved presets</Small>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          ) : (
            <DropdownMenuRadioGroup
              label="Presets"
              value={selectedPresetId ?? ''}
              onValueChange={onPresetApply}
            >
              {presets.map((preset) => (
                <TablePresetsItem
                  key={preset.id}
                  preset={preset}
                  isDefault={defaultPresetId === preset.id}
                  onSetDefault={() => handleSetDefault(preset.id)}
                  onClearDefault={handleClearDefault}
                  onRename={() => handleRenamePreset(preset)}
                  onDuplicate={() => handleDuplicatePreset(preset)}
                  onDelete={() => handleDeletePreset(preset.id)}
                />
              ))}
            </DropdownMenuRadioGroup>
          )}
          <DropdownMenuGroup>
            {selectedPresetId && isPresetDirty ? (
              <>
                <DropdownMenuItem
                  onClick={() => {
                    mainDropdownState.state.close();
                    onUpdatePreset();
                  }}
                  icon="save"
                >
                  Update preset
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    mainDropdownState.state.close();
                    saveDialogState.openFromMenu();
                  }}
                  icon="copy"
                >
                  Save preset as…
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    mainDropdownState.state.close();
                    onPresetApply(selectedPresetId);
                  }}
                  icon="undo"
                >
                  Discard changes
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem
                onClick={() => {
                  mainDropdownState.state.close();
                  saveDialogState.openFromMenu();
                }}
                icon="save"
                disabled={!!selectedPresetId}
              >
                Save preset
              </DropdownMenuItem>
            )}
            {selectedPresetId && (
              <DropdownMenuItem
                onClick={() => {
                  mainDropdownState.state.close();
                  onPresetClear();
                }}
                icon="cross"
              >
                Clear preset
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuPopover>
      </DropdownMenu>
      <TablePresetsNameDialog onSave={handleSavePreset} state={saveDialogState.state} />
      <TablePresetsNameDialog
        state={renameDialogState.state}
        onSave={handleConfirmRename}
        initialLabel={presetToRename?.name ?? ''}
        heading="Rename preset"
        submitLabel="Rename"
      />
      <TablePresetsNameDialog
        state={duplicateDialogState.state}
        onSave={handleConfirmDuplicate}
        initialLabel={presetToDuplicate ? `${presetToDuplicate.name} (Copy)` : ''}
      />
      <TablePresetsDeleteDialog
        onDelete={handleConfirmDelete}
        state={deleteDialogState.state}
        label={presetToDelete ? presetToDelete.name : 'preset'}
      />
      <TablePresetsDefaultDialog
        onSetDefault={handleConfirmSetDefault}
        state={defaultDialogState.state}
        label={presetToSetDefault ? presetToSetDefault.name : 'preset'}
      />
    </>
  );
}
